import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Check, X, Globe2, FileText, Lock, ClipboardCheck,
  Users2, Radar, Anchor, Plane, Building2, Landmark, Briefcase, Mountain,
  Eye, UserCheck, Award, Star, ArrowRight, MonitorSmartphone, Video,
  MessageSquare, ClipboardList, LineChart, ScrollText, MapPin,
} from "lucide-react";

/**
 * Public Elite Protection Network landing page.
 * URL: /elite-protection-network
 * Scoped visual system (orange + dark navy) via .elite-page class in index.css.
 * DOES NOT touch existing auth, dashboards or platform routes.
 */
const HERO_IMG = "https://images.unsplash.com/photo-1615663245857-ac93bb7c39e7?auto=format&fit=crop&w=1600&q=80";
const OPP_IMAGES = {
  exec: "https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=800&q=80",
  family: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80",
  corp: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80",
  diplo: "https://images.unsplash.com/photo-1541872703-74c5e44368f9?auto=format&fit=crop&w=800&q=80",
  maritime: "https://images.unsplash.com/photo-1517400508447-f8dd518b86db?auto=format&fit=crop&w=800&q=80",
  highrisk: "https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=800&q=80",
};

const NAV = [
  { id: "elite-network", label: "Elite Network" },
  { id: "who-its-for", label: "Who It’s For" },
  { id: "opportunities", label: "Opportunities" },
  { id: "how-it-works", label: "How It Works" },
  { id: "standards", label: "Standards" },
  { id: "international", label: "International" },
  { id: "resources", label: "Resources" },
];

export default function EliteProtectionNetwork() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Elite Protection Network | Global Close Protection Opportunities | SecuSource";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", "Join SecuSource’s Elite Protection Network for verified Close Protection, executive security and specialist deployment opportunities in the UK, Ireland and internationally.");
    return () => { document.title = prevTitle; if (meta && prevDesc) meta.setAttribute("content", prevDesc); };
  }, []);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="elite-page" data-testid="elite-page">
      <TopNav scrollTo={scrollTo} />

      {/* Hero */}
      <section id="elite-network" className="elite-hero" data-testid="elite-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          <span className="elite-eyebrow">Elite Protection Network</span>
          <h1 className="elite-h1 mt-4">
            PROTECTING PEOPLE.<br /><span className="accent">PROTECTING REPUTATIONS.</span>
          </h1>
          <div className="mt-4 font-outfit font-semibold text-lg text-white/95">Global Opportunities. Elite Standards. Stronger Futures.</div>
          <p className="elite-sub mt-4 max-w-2xl">
            Join a global network of professional Close Protection Officers, security specialists and operational experts. Access high-end deployments, international opportunities and the tools needed to perform at your best.
          </p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Link to="/register/staff" className="elite-btn elite-btn-primary" data-testid="elite-cta-join-hero">Join the Elite Network</Link>
            <a href="#opportunities" onClick={scrollTo("opportunities")} className="elite-btn elite-btn-ghost" data-testid="elite-cta-explore">Explore Opportunities</a>
          </div>
          <div className="mt-6 elite-trust" style={{ color: "rgba(255,255,255,0.8)" }}>
            <ShieldCheck className="w-4 h-4" /> Every Elite deployment is manually reviewed by experienced protection professionals.
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="!py-8" style={{ background: "var(--e-bg)" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              [UserCheck, "Verified Professionals"],
              [ClipboardCheck, "Elite Operations Review"],
              [Lock, "Secure Negotiation"],
              [FileText, "Compliance Reports"],
              [ScrollText, "Audit Trail"],
              [Globe2, "Global Opportunities"],
            ].map(([Icon, label]) => (
              <div key={label} className="flex items-center gap-2.5" data-testid={`trust-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-9 h-9 rounded-md border" style={{ borderColor: "var(--e-border)", background: "rgba(255,114,0,0.06)" }}>
                  <Icon className="w-4 h-4 m-2.5" style={{ color: "var(--e-orange)" }} />
                </div>
                <span className="font-outfit font-semibold text-sm text-white">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section id="who-its-for">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead
            eyebrow="Who it’s for"
            title="Built for Experienced Protection Professionals"
            sub="SecuSource welcomes professionals from a range of security, protection and operational backgrounds. Your suitability is based on verified skills, experience, qualifications, references and professional conduct—not one specific career route."
          />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { Icon: ShieldCheck, t: "Close Protection Officers", d: "Experienced professionals protecting individuals, families and executives." },
              { Icon: Users2, t: "Security Managers", d: "Professionals leading teams, operations and complex deployments." },
              { Icon: LineChart, t: "Risk & Intelligence Specialists", d: "Professionals supporting planning, assessment and informed decision-making." },
              { Icon: Award, t: "Former Police Personnel", d: "Professionals bringing operational awareness, investigation and public-safety experience." },
              { Icon: Star, t: "Military Veterans", d: "Professionals bringing discipline, leadership, teamwork and operational experience." },
              { Icon: Briefcase, t: "Private Security Professionals", d: "Experienced personnel working in specialist, corporate or high-risk environments." },
              { Icon: Anchor, t: "Maritime & Aviation Security", d: "Professionals supporting vessels, ports, aviation and international movement." },
              { Icon: Eye, t: "Surveillance & Operational Specialists", d: "Professionals supporting discreet protection, intelligence and operational assignments." },
            ].map(({ Icon, t, d }) => (
              <div key={t} className="elite-card" data-testid={`who-card-${t.replace(/\W+/g, "-").toLowerCase()}`}>
                <div className="ico"><Icon className="w-5 h-5" /></div>
                <div className="font-outfit font-bold text-white text-base">{t}</div>
                <p className="elite-sub text-sm mt-1.5">{d}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8 font-outfit font-semibold uppercase tracking-widest text-sm" style={{ color: "var(--e-orange)" }}>
            Different backgrounds. One professional standard.
          </div>
        </div>
      </section>

      {/* Opportunities */}
      <section id="opportunities">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead eyebrow="Opportunities" title="Elite Opportunities. Worldwide." />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { img: OPP_IMAGES.exec, Icon: ShieldCheck, t: "Executive Protection", d: "Protection for executives, public figures and high-profile individuals." },
              { img: OPP_IMAGES.family, Icon: Building2, t: "Family Office Protection", d: "Discreet support for private clients, families, residences and travel." },
              { img: OPP_IMAGES.corp, Icon: Briefcase, t: "Corporate Security", d: "Professional protection and operational support for organisations and leadership teams." },
              { img: OPP_IMAGES.diplo, Icon: Landmark, t: "Diplomatic & Government Support", d: "Specialist assignments requiring discretion, protocol and advanced preparation." },
              { img: OPP_IMAGES.maritime, Icon: Anchor, t: "Maritime & Aviation Security", d: "Protection connected to vessels, ports, aircraft and international travel." },
              { img: OPP_IMAGES.highrisk, Icon: Mountain, t: "High-Risk & Specialist Operations", d: "Carefully assessed deployments requiring advanced experience and capabilities." },
            ].map((o) => (
              <div key={o.t} className="elite-opp-card" data-testid={`opp-${o.t.replace(/\W+/g, "-").toLowerCase()}`}>
                <img src={o.img} alt={o.t} loading="lazy" />
                <div className="body">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: "rgba(255,114,0,0.15)", border: "1px solid rgba(255,114,0,0.35)" }}>
                      <o.Icon className="w-4 h-4" style={{ color: "var(--e-orange)" }} />
                    </div>
                    <span className="elite-eyebrow" style={{ color: "var(--e-orange-soft)" }}>Elite</span>
                  </div>
                  <div className="font-outfit font-bold text-white text-lg leading-tight">{o.t}</div>
                  <div className="elite-sub text-xs mt-1">{o.d}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="elite-mute text-center mt-8 max-w-2xl mx-auto">
            Opportunity availability depends on verified experience, licensing, qualifications, location and client requirements.
          </p>
        </div>
      </section>

      {/* International */}
      <section id="international">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead eyebrow="International" title="Your Experience Can Travel"
            sub="Build a professional profile that can be considered for suitable opportunities in the UK, Ireland and internationally. SecuSource helps verified professionals demonstrate their skills, experience, availability and deployment history to approved companies and clients." />
          <div className="elite-map p-6 sm:p-10">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                "United Kingdom & Ireland", "Europe", "Middle East", "Africa",
                "Asia-Pacific", "The Americas", "Maritime", "International Travel Assignments",
              ].map((label, i) => (
                <div key={label} className="flex items-center gap-2 text-white/90 font-manrope text-sm">
                  <MapPin className="w-4 h-4" style={{ color: "var(--e-orange)" }} />
                  {label}
                </div>
              ))}
            </div>
            {/* subtle pins */}
            <div className="relative mt-8 h-40 hidden sm:block">
              {[
                [15, 30], [30, 20], [42, 45], [60, 30], [72, 55], [85, 40], [50, 70], [22, 65],
              ].map(([x, y], i) => (
                <span key={i} className="elite-pin" style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${i * 0.3}s` }} />
              ))}
            </div>
          </div>
          <p className="elite-mute mt-6">All opportunities remain subject to local licensing, immigration, visa, insurance and regulatory requirements.</p>
        </div>
      </section>

      {/* Problem / Difference */}
      <section>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead eyebrow="The Difference" title="Professional Experience Should Not Disappear Between Deployments" />
          <div className="grid gap-5 lg:grid-cols-2">
            <div className="elite-card">
              <div className="elite-eyebrow" style={{ color: "var(--e-text-3)" }}>The current problem</div>
              <ul className="space-y-2.5 mt-3">
                {[
                  "Experience is difficult to demonstrate",
                  "References and qualifications are scattered",
                  "Professionals repeatedly chase individual jobs",
                  "Trusted colleagues are separated",
                  "Last-minute deployments rely on WhatsApp",
                  "Good performance does not build a portable reputation",
                  "Operational documents are spread across different systems",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm elite-sub"><span className="elite-cross"><X className="w-3 h-3" /></span>{t}</li>
                ))}
              </ul>
            </div>
            <div className="elite-card" style={{ borderColor: "var(--e-border)" }}>
              <div className="elite-eyebrow">The SecuSource difference</div>
              <ul className="space-y-2.5 mt-3">
                {[
                  "One verified professional profile",
                  "Trusted teams and professional networks",
                  "References and deployment history",
                  "Access to suitable specialist opportunities",
                  "Secure deployment rooms",
                  "Briefings, SOPs and documents in one place",
                  "Performance builds professional standing",
                  "International visibility where legally eligible",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-white"><span className="elite-check"><Check className="w-3 h-3" /></span>{t}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Core benefits */}
      <section>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead eyebrow="Benefits" title="Why Professionals Join the Elite Network" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { Icon: UserCheck, t: "Get Recognised", d: "Your verified skills, experience, qualifications and performance are presented through one professional profile." },
              { Icon: Users2, t: "Build Trusted Teams", d: "Connect with professionals you know and trust, or become part of a carefully assembled deployment team." },
              { Icon: Globe2, t: "Access Specialist Opportunities", d: "Be considered for executive, corporate, private-client and international protection deployments." },
              { Icon: MonitorSmartphone, t: "Professional Deployment Software", d: "Access briefings, SOPs, risk assessments, secure documents, live updates and deployment support in one place." },
              { Icon: Award, t: "Build a Verified Reputation", d: "Successful deployments, professional references and client feedback strengthen your standing over time." },
              { Icon: Lock, t: "Secure & Transparent Operations", d: "Work through controlled deployment rooms with clear records, agreements, responsibilities and audit trails." },
            ].map(({ Icon, t, d }) => (
              <div key={t} className="elite-card" data-testid={`benefit-${t.replace(/\W+/g, "-").toLowerCase()}`}>
                <div className="ico"><Icon className="w-5 h-5" /></div>
                <div className="font-outfit font-bold text-white">{t}</div>
                <p className="elite-sub text-sm mt-1.5">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead eyebrow="Process" title="Your Elite Network Journey" />
          <div className="grid gap-6 lg:grid-cols-6 relative">
            {[
              { t: "Create Your Profile", d: "Upload your experience, qualifications, capabilities and professional history." },
              { t: "Elite Operations Review", d: "The operations team reviews your information, documents and references." },
              { t: "Become Verified", d: "Approved professionals receive verified status for suitable opportunities." },
              { t: "Access Opportunities", d: "View or receive invitations to deployments that match your capabilities and availability." },
              { t: "Deploy Professionally", d: "Access the secure Deployment Room, briefings, SOPs, documents and live operational support." },
              { t: "Build Your Reputation", d: "Professional performance, references and completed assignments strengthen your profile." },
            ].map((s, i) => (
              <div key={s.t} className="relative">
                <div className="elite-step-num">{i + 1}</div>
                {i < 5 && <div className="elite-step-line hidden lg:block absolute top-5 left-[52px] right-[-24px]" />}
                <div className="font-outfit font-bold text-white text-sm mt-4">{s.t}</div>
                <p className="elite-mute text-xs mt-1.5 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Standards */}
      <section id="standards">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead eyebrow="Standards" title="Elite Status is Earned" />
          <div className="grid gap-5 lg:grid-cols-3">
            <div className="elite-card">
              <span className="elite-status-amber">Amber · Provisional</span>
              <ul className="space-y-2 mt-4 elite-sub text-sm">
                <li className="flex items-start gap-2"><span className="elite-check"><Check className="w-3 h-3" /></span>Profile submitted</li>
                <li className="flex items-start gap-2"><span className="elite-check"><Check className="w-3 h-3" /></span>Information awaiting review</li>
                <li className="flex items-start gap-2"><span className="elite-check"><Check className="w-3 h-3" /></span>Documents being assessed</li>
                <li className="flex items-start gap-2 text-white/70"><span className="elite-cross"><X className="w-3 h-3" /></span>Not yet eligible for Elite deployment</li>
              </ul>
            </div>
            <div className="elite-card" style={{ borderColor: "var(--e-orange)" }}>
              <span className="elite-eyebrow">Elite Operations Review</span>
              <ul className="space-y-2 mt-4 elite-sub text-sm">
                {["Identity reviewed", "Licences and qualifications reviewed", "Experience assessed", "References reviewed", "Capabilities evaluated"].map((t) => (
                  <li key={t} className="flex items-start gap-2"><span className="elite-check"><Check className="w-3 h-3" /></span>{t}</li>
                ))}
              </ul>
            </div>
            <div className="elite-card">
              <span className="elite-status-green">Verified · Green</span>
              <ul className="space-y-2 mt-4 elite-sub text-sm">
                {["Approved for suitable opportunities", "Visible to authorised Elite Operations users", "Eligible for team selection", "Performance tracked after deployment"].map((t) => (
                  <li key={t} className="flex items-start gap-2"><span className="elite-check"><Check className="w-3 h-3" /></span>{t}</li>
                ))}
              </ul>
            </div>
          </div>
          <p className="elite-mute mt-6">Verification is not permanent entitlement. Serious concerns, expired documents or repeated poor performance can trigger review, suspension or removal.</p>
        </div>
      </section>

      {/* Deployment software */}
      <section id="resources">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead eyebrow="Deployment Software" title="Professional Software for Professional Deployments" />
          <div className="grid gap-6 lg:grid-cols-2 items-center">
            <div className="elite-card p-8">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  [ClipboardList, "Secure job briefings"],
                  [Users2, "Team roster and role allocation"],
                  [ScrollText, "SOPs and risk assessments"],
                  [ClipboardCheck, "Document acknowledgement"],
                  [MessageSquare, "Secure team communication"],
                  [UserCheck, "Check-in and attendance"],
                  [Radar, "Incident reporting"],
                  [Video, "Video briefing access"],
                  [LineChart, "Live operational updates"],
                  [FileText, "Compliance record"],
                  [ScrollText, "Post-deployment reporting"],
                ].map(([Icon, t]) => (
                  <div key={t} className="flex items-center gap-2 elite-sub">
                    <Icon className="w-4 h-4" style={{ color: "var(--e-orange)" }} /> {t}
                  </div>
                ))}
              </div>
              <p className="elite-mute mt-5">Access to deployment tools is included while working through an approved SecuSource deployment.</p>
            </div>
            <div className="elite-card p-8 flex items-center justify-center min-h-[340px]" style={{
              background: "radial-gradient(500px 240px at 60% 40%, rgba(255,114,0,0.18), transparent 60%), var(--e-card-hi)",
            }}>
              <div className="text-center">
                <MonitorSmartphone className="w-16 h-16 mx-auto mb-3" style={{ color: "var(--e-orange)" }} />
                <div className="font-outfit font-bold text-white">Operations Room · Mobile &amp; Desktop</div>
                <div className="elite-mute mt-1 text-xs">Secure briefings · Live status · Compliance</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <span className="elite-eyebrow">Elite Protection Network</span>
          <h2 className="elite-h1 mt-4">
            YOUR BACKGROUND BUILT THE EXPERIENCE.<br />
            <span className="accent">SECUSOURCE HELPS IT GET RECOGNISED.</span>
          </h2>
          <p className="elite-sub max-w-2xl mx-auto mt-5">
            Whether your experience comes from Close Protection, private security, policing, the military, intelligence, maritime operations or specialist security work, your professional record should open more doors.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-7">
            <Link to="/register/staff" className="elite-btn elite-btn-primary" data-testid="elite-cta-join-final">Join the Elite Network</Link>
            <a href="#standards" onClick={scrollTo("standards")} className="elite-btn elite-btn-ghost" data-testid="elite-cta-verify">View How Verification Works</a>
          </div>
          <p className="elite-mute mt-4">Registration is free. Eligibility for Elite status is subject to review and verification.</p>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: "var(--e-bg)", borderTop: "1px solid rgba(255,133,0,0.2)" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
          <div className="elite-divider" />
          <div className="text-center font-outfit font-bold text-white text-xl tracking-widest uppercase">
            Elite Standards. Trusted Professionals. Global Futures.
          </div>
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6 mt-8">
            {[
              [Globe2, "Global Network"],
              [UserCheck, "Verified Professionals"],
              [Award, "Professional Standards"],
              [Lock, "Secure Operations"],
              [FileText, "Compliance Records"],
              [Briefcase, "Real Opportunities"],
            ].map(([Icon, label]) => (
              <div key={label} className="elite-trust justify-center sm:justify-start">
                <Icon className="w-4 h-4" />
                <span>{label}</span>
              </div>
            ))}
          </div>
          <div className="elite-mute text-center mt-10">© SecuSource · Elite Protection Network</div>
        </div>
      </footer>
    </div>
  );
}

function TopNav({ scrollTo }) {
  return (
    <div className="elite-nav">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2" data-testid="elite-brand">
          <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: "var(--e-orange)", boxShadow: "0 8px 24px -8px rgba(255,114,0,0.7)" }}>
            <ShieldCheck className="w-4 h-4 text-black" strokeWidth={2.4} />
          </div>
          <div className="leading-tight">
            <div className="font-outfit font-bold text-white text-sm tracking-tight">SecuSource</div>
            <div className="text-[9px] uppercase tracking-widest" style={{ color: "var(--e-orange-soft)" }}>Elite Protection Network</div>
          </div>
        </Link>
        <nav className="hidden lg:flex items-center gap-5 ml-4 flex-1">
          {NAV.map((n) => (
            <a key={n.id} href={`#${n.id}`} onClick={scrollTo(n.id)} className="link" data-testid={`elite-nav-${n.id}`}>{n.label}</a>
          ))}
        </nav>
        <div className="flex items-center gap-2 ml-auto">
          <Link to="/login" className="elite-btn elite-btn-ghost !py-2 !px-4 !text-xs" data-testid="elite-nav-login">Log In</Link>
          <Link to="/register/staff" className="elite-btn elite-btn-primary !py-2 !px-4 !text-xs" data-testid="elite-nav-join">Join Elite Network</Link>
        </div>
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, sub }) {
  return (
    <div className="text-center mb-10">
      <span className="elite-eyebrow">{eyebrow}</span>
      <h2 className="elite-h2 mt-3">{title}</h2>
      <div className="elite-divider mt-4" />
      {sub && <p className="elite-sub max-w-3xl mx-auto">{sub}</p>}
    </div>
  );
}
